import type { Plant } from '@/data/plants';
import {
  addDays,
  calculateSeedStartingDates,
  type CalculatedPlantingWindow,
} from '@/lib/seedStartingCalculator';

export type GardenTask = {
  id: string;
  plantId?: string;
  type: 'start' | 'sow' | 'transplant' | 'harden' | 'frost';
  title: string;
  description?: string;
  dateStart: Date;
  dateEnd?: Date;
  priority: 'low' | 'medium' | 'high';
  shouldNotify?: boolean;
};

const PRIORITY_ORDER: Record<GardenTask['priority'], number> = {
  high: 0,
  medium: 1,
  low: 2,
};

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function dateKey(date: Date): string {
  return startOfDay(date).toISOString().slice(0, 10);
}

function daysBetween(start: Date, end: Date): number {
  return Math.round((startOfDay(end).getTime() - startOfDay(start).getTime()) / 86_400_000);
}

function isRelevantWindow(today: Date, dateStart: Date, dateEnd: Date): boolean {
  const earliestVisible = addDays(dateStart, -3);
  const current = startOfDay(today).getTime();

  return current >= startOfDay(earliestVisible).getTime() &&
    current <= startOfDay(dateEnd).getTime();
}

function priorityFor(today: Date, dateStart: Date, dateEnd: Date): GardenTask['priority'] {
  const daysUntilStart = daysBetween(today, dateStart);
  const daysUntilEnd = daysBetween(today, dateEnd);

  if (daysUntilStart <= 0 && daysUntilEnd >= 0) return 'high';
  if (daysUntilStart > 0 && daysUntilStart <= 1) return 'high';
  if (daysUntilStart > 1 && daysUntilStart <= 3) return 'medium';
  return 'low';
}

function isSameGardenDay(a: Date, b: Date): boolean {
  return startOfDay(a).getTime() === startOfDay(b).getTime();
}

function shouldNotifyFor(type: GardenTask['type'], today: Date, dateStart: Date): boolean {
  return (
    (type === 'start' || type === 'harden' || type === 'transplant') &&
    isSameGardenDay(today, dateStart)
  );
}

function usableWindow(window: CalculatedPlantingWindow | undefined): { start: Date; end: Date } | null {
  if (!window || window.status !== 'dated' || !window.startDate || !window.endDate) return null;
  return {
    start: startOfDay(window.startDate),
    end: startOfDay(window.endDate),
  };
}

function titleTiming(today: Date, dateStart: Date, dateEnd: Date): string {
  const startsIn = daysBetween(today, dateStart);
  const endsIn = daysBetween(today, dateEnd);

  if (startsIn > 0 && startsIn <= 3) return `in ${startsIn} days`;
  if (startsIn > 3 && startsIn <= 6) return 'this week';
  if (startsIn > 6) return 'next week';
  if (startsIn <= 0 && endsIn >= 0) return 'now';
  return 'now';
}

function taskTitle(type: GardenTask['type'], plantName: string, today: Date, dateStart: Date, dateEnd: Date): string {
  const timing = titleTiming(today, dateStart, dateEnd);

  if (type === 'start') return `Start ${plantName} seeds ${timing}`;
  if (type === 'sow') return `Direct sow ${plantName} ${timing}`;
  if (type === 'transplant') return `Transplant ${plantName} ${timing}`;
  if (type === 'harden') return `Harden off ${plantName} ${timing}`;
  return `Protect ${plantName} ${timing}`;
}

function taskDescription(type: GardenTask['type']): string {
  if (type === 'start') return 'Start indoors now so seedlings are ready by transplant time.';
  if (type === 'sow') return 'This is a strong sowing window in your area.';
  if (type === 'transplant') return 'Seedlings should be ready to move outdoors soon.';
  if (type === 'harden') return 'Begin gradual outdoor exposure before transplanting.';
  return 'Tender crops may need protection if temperatures dip.';
}

function createWindowTask({
  plant,
  type,
  window,
  today,
}: {
  plant: Plant;
  type: GardenTask['type'];
  window: CalculatedPlantingWindow | undefined;
  today: Date;
}): GardenTask | null {
  if (!window) return null;
  const dates = usableWindow(window);
  if (!dates) return null;
  if (!isRelevantWindow(today, dates.start, dates.end)) return null;

  return {
    id: `garden-task:${plant.id}:${type}:${dateKey(dates.start)}`,
    plantId: plant.id,
    type,
    title: taskTitle(type, plant.name, today, dates.start, dates.end),
    description: taskDescription(type),
    dateStart: dates.start,
    dateEnd: dates.end,
    priority: priorityFor(today, dates.start, dates.end),
    shouldNotify: shouldNotifyFor(type, today, dates.start),
  };
}

function createHardeningTask({
  plant,
  transplantWindow,
  today,
}: {
  plant: Plant;
  transplantWindow: CalculatedPlantingWindow | undefined;
  today: Date;
}): GardenTask | null {
  const dates = usableWindow(transplantWindow);
  if (!dates) return null;

  const hardenStart = addDays(dates.start, -7);
  const hardenEnd = dates.end.getTime() === dates.start.getTime()
    ? hardenStart
    : addDays(dates.end, -7);

  if (!isRelevantWindow(today, hardenStart, hardenEnd)) return null;

  return {
    id: `garden-task:${plant.id}:harden:${dateKey(hardenStart)}`,
    plantId: plant.id,
    type: 'harden',
    title: taskTitle('harden', plant.name, today, hardenStart, hardenEnd),
    description: taskDescription('harden'),
    dateStart: hardenStart,
    dateEnd: hardenEnd,
    priority: priorityFor(today, hardenStart, hardenEnd),
    shouldNotify: shouldNotifyFor('harden', today, hardenStart),
  };
}

export function generateGardenTasks(params: {
  zone: number;
  plants: Plant[];
  today: Date;
}): GardenTask[] {
  const { zone, plants, today } = params;
  const tasks: GardenTask[] = [];
  const years = Array.from(new Set([today.getFullYear(), today.getFullYear() + 1]));

  for (const plant of plants) {
    for (const year of years) {
      const calculation = calculateSeedStartingDates(plant, zone, year);

      const startTask = createWindowTask({
        plant,
        type: 'start',
        window: calculation.indoorStart,
        today,
      });
      if (startTask) tasks.push(startTask);

      const sowTask = createWindowTask({
        plant,
        type: 'sow',
        window: calculation.directSow,
        today,
      });
      if (sowTask) tasks.push(sowTask);

      const transplantTask = createWindowTask({
        plant,
        type: 'transplant',
        window: calculation.transplant,
        today,
      });
      if (transplantTask) tasks.push(transplantTask);

      if (calculation.mode !== 'transplant-only') {
        const hardeningTask = createHardeningTask({
          plant,
          transplantWindow: calculation.transplant,
          today,
        });
        if (hardeningTask) tasks.push(hardeningTask);
      }
    }
  }

  const deduped = Array.from(
    new Map(tasks.map(task => [task.id, task])).values(),
  );

  return deduped.sort((a, b) => {
    const priorityDiff = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
    if (priorityDiff !== 0) return priorityDiff;
    return a.dateStart.getTime() - b.dateStart.getTime();
  });
}
